'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useState } from 'react'
import type { Channel as ChannelInstance, Event, MessageResponse } from 'stream-chat'
import { StreamChat } from 'stream-chat'

import { envConfig } from '../lib/env-config'
import { tokenUtils } from '../utils/token-utils'
import { useBrowserNotification } from './use-browser-notification'

const ACTIVE_CHAT_KEY = 'activeChatID'

export function useChatClient() {
  const router = useRouter()
  const pathname = usePathname()
  const { create } = useBrowserNotification()

  const [client] = useState(() =>
    StreamChat.getInstance(envConfig.stream.appKey ?? '', {
      enableWSFallback: true,
      timeout: 30000
    })
  )

  const [activeChatID, setActiveChatID] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACTIVE_CHAT_KEY)
    }
    return null
  })

  const loggedInUserID = tokenUtils.getUserID() || ''

  // Handle new message event
  const handleNewMessage = (event: Event) => {
    const message = event.message as MessageResponse & { action_type?: string }
    if (message?.action_type) return

    if (
      loggedInUserID !== event?.user?.id &&
      event.message &&
      event.message.user &&
      event.channel_id !== activeChatID
    ) {
      create({
        body: event.message.text || '',
        data: { messageID: event.message.id, silent: event.message.silent },
        notificationHandler: () => {
          router.push(`/user/inbox?chatID=${event.channel_id}&chatType=${event.channel_type}`)
        },
        title: event.message.user?.name ? `${event.message.user.name} sent a message` : ''
      })
    }
  }

  // Handle new message notification
  const handleNewMessageNotification = (event: Event) => {
    const message = event.message as MessageResponse & { action_type?: string }
    if (message?.action_type) return

    if (
      event.message &&
      event.message.user &&
      loggedInUserID !== event?.message.user.id &&
      event.channel_id !== activeChatID
    ) {
      create({
        body: event.message.text || '',
        data: { messageID: event.message.id },
        notificationHandler: () => {
          router.push(`/user/inbox?chatID=${event.channel_id}&chatType=${event.channel_type}`)
        },
        title: event.message.user?.name ? `${event.message.user.name} sent a message` : ''
      })
    }
  }

  // Handle storage events from other tabs
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === ACTIVE_CHAT_KEY && event.url?.indexOf('inbox') !== 0) {
      setActiveChatID(event.newValue)
    }
  }

  const handleBeforeUnload = () => {
    if (!pathname.startsWith('/inbox')) {
      localStorage.setItem(ACTIVE_CHAT_KEY, '')
    }
  }

  // Add chat listeners
  const addListeners = () => {
    client.on('message.new', handleNewMessage)
    client.on('notification.message_new', handleNewMessageNotification)

    // Add localStorage listener for cross-tab communication
    window.addEventListener('storage', handleStorageChange)

    window.addEventListener('beforeunload', handleBeforeUnload)
  }

  // Set active chat
  const setActiveChat = (channel: ChannelInstance | null) => {
    const newActiveChatID = channel?.id || null
    setActiveChatID(newActiveChatID)

    if (!pathname.startsWith('/inbox')) {
      localStorage.setItem(ACTIVE_CHAT_KEY, newActiveChatID || '')
    }
  }

  return {
    activeChatID,
    addListeners,
    client,
    removeListeners: () => {
      client.off('message.new', handleNewMessage)
      client.off('notification.message_new', handleNewMessageNotification)
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    },
    setActiveChat
  }
}
